import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  fetchNotifications,
  fetchUnreadCount,
  markAllRead,
  markNotificationRead,
} from '@/api/notifications'
import type { MarkAllReadResponse, NotificationsPage, UnreadCount } from '@/api/types'

const THIRTY_SECONDS = 30 * 1000

export const useMyNotifications = (page: number, size: number = 20) =>
  useQuery<NotificationsPage>({
    queryKey: ['notifications', page, size],
    queryFn: () => fetchNotifications(page, size),
    staleTime: THIRTY_SECONDS,
  })

/**
 * Bell-badge poll. {@code enabled} should reflect whether the viewer is signed in — anonymous
 * viewers see no bell at all per the navbar mount logic, but the hook itself defends-in-depth.
 */
export const useUnreadNotificationCount = (enabled: boolean = true) =>
  useQuery<UnreadCount>({
    queryKey: ['notifications', 'unread-count'],
    queryFn: fetchUnreadCount,
    enabled,
    staleTime: THIRTY_SECONDS,
    refetchOnWindowFocus: true,
  })

export const useMarkNotificationRead = () => {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (id) => markNotificationRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}

export const useMarkAllRead = () => {
  const qc = useQueryClient()
  return useMutation<MarkAllReadResponse, Error, void>({
    mutationFn: () => markAllRead(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}
